import React from 'react';
import { useNavigate } from 'react-router';
import { CardSize } from 'components/CardSizeSelector/CardSizeSelector';
import SwapDiagram from './SwapDiagram';
import FundingSteps from './FundingSteps';
import SuggestionsStrip from './SuggestionsStrip';
import ReadinessChecklist, { useReadinessData } from './ReadinessChecklist';
import SampleOfferCard from './SampleOfferCard';
import './OffersZeroState.css';

interface OffersZeroStateProps {
  tab: 'received' | 'sent';
  cardSize?: CardSize;
}

/**
 * An empty inbox becomes a starting point: hero explainer with swap diagram,
 * the three-step funding explainer, up to four proposable trades, the
 * readiness checklist, and a sample offer. Data-dependent blocks omit
 * themselves on error — a backend gap never blanks the hero. The hero body
 * paragraph is the only body copy the hero gets (approved Final Copy).
 */
const OffersZeroState: React.FC<OffersZeroStateProps> = () => {
  const navigate = useNavigate();
  const readiness = useReadinessData();

  return (
    <div className="offers-zero">
      <section className="offers-zero-hero" aria-label="What trade offers are">
        <div className="offers-zero-hero-text">
          <div className="offers-zero-eyebrow">
            <span />
            NEW · TWO-SIDED TRADES
          </div>
          <h2 className="offers-zero-hero-heading">Swap cards straight across.</h2>
          <p className="offers-zero-hero-body">
            A trade offer names exactly which cards leave your collection and which ones arrive,
            with both sides priced and locked. Your partner accepts or declines, with no haggling
            and no waiting for a one-way match.
          </p>
          <div className="offers-zero-actions">
            <button
              type="button"
              className="offers-zero-cta"
              // Router state, not a query param: /search preserves any param it
              // does not own (SearchWorkbenchPage clears only OWNED_URL_PARAMS),
              // so an ?intent= flag would stick to the URL through every later
              // search and re-fire the coachmark on each one. Location state is
              // scoped to this navigation and clears itself, which is the
              // per-arrival behavior the coachmark wants.
              onClick={() =>
                navigate('/search?availableToReceive=true', { state: { offerIntent: true } })
              }
            >
              Find someone to trade with
            </button>
            <a href="#offers-sample">See how an offer works</a>
          </div>
        </div>
        <SwapDiagram
          haveScryfallId={readiness.samples.haveScryfallId}
          wantScryfallId={readiness.samples.wantScryfallId}
        />
      </section>

      <FundingSteps />

      <SuggestionsStrip />

      {!readiness.isLoading &&
        !readiness.hasError &&
        readiness.hasPublicHaveList != null &&
        readiness.wantListCount != null &&
        readiness.notForTradeCount != null && (
          <ReadinessChecklist
            hasPublicHaveList={readiness.hasPublicHaveList}
            wantListCount={readiness.wantListCount}
            notForTradeCount={readiness.notForTradeCount}
          />
        )}

      <SampleOfferCard />
    </div>
  );
};

export default OffersZeroState;
